// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for the WS8a zone frame (foundations-first reorder). Computes
// the bounded, non-overlapping rectangle for each of the five zones — top sliver,
// left Tools, center Model, right Activities, bottom Results — from the container
// size and the user's resize/visibility state. This is the structural fix for the
// overlap class of bug (NC-1): surfaces no longer float at absolute offsets; each
// lives inside a zone rect this function hands it.
//
// The acceptance-spec invariants this guarantees:
//   - the Model is never covered (it is its own bounded rect; nothing overlaps it),
//   - zones never overlap and tile the container,
//   - the Model keeps a minimum readable size, so dragging a rail/band to an
//     extreme can never collapse or clip the canvas.
//
// Density presets and pin/unpin resolve to the sizes/visibility passed in here
// (see the density mapping, built separately); this module is layout-only and
// density-agnostic, which keeps it trivially testable.

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export type ZoneId = 'sliver' | 'tools' | 'model' | 'activities' | 'results';

export interface ZoneFrameLayout {
  readonly sliver: Rect;
  readonly tools: Rect;
  readonly model: Rect;
  readonly activities: Rect;
  readonly results: Rect;
}

export interface ZoneFrameInput {
  /** Container (editor) size. */
  readonly width: number;
  readonly height: number;
  /** Fixed top-sliver height (mode switch + File + SFD/CLD + density + account). */
  readonly sliverHeight: number;
  /** Left Tools rail width; 0 when the rail is unpinned/hidden. */
  readonly toolsWidth: number;
  /** Requested right Activities rail width (drag-resizable); <= 0 hides it. */
  readonly activitiesWidth: number;
  /** Requested bottom Results band height (drag-resizable); <= 0 hides it. */
  readonly resultsHeight: number;
}

// Minimums so the Model — the primary artifact, never covered — cannot be
// collapsed or clipped by an extreme drag on a neighboring zone.
export const MIN_MODEL_WIDTH = 200;
export const MIN_MODEL_HEIGHT = 160;
export const MIN_ACTIVITIES_WIDTH = 240;
export const MIN_RESULTS_HEIGHT = 96;

function clamp(value: number, lo: number, hi: number): number {
  if (!Number.isFinite(value)) {
    return lo;
  }
  return Math.max(lo, Math.min(hi, value));
}

/**
 * Compute the five zone rectangles for the current container + resize state.
 *
 * Layout: the sliver spans the full width at the top. Below it, Tools (left) and
 * Activities (right) take the full remaining height on the sides; the center
 * column between them is split vertically into Model (top) and Results (bottom).
 * Because Results shares the center column with the Model, growing/maximizing
 * Results shrinks the Model region (never Tools/Activities) — matching the
 * "maximize = reflow, Model never covered" rule.
 */
export function computeZoneLayout(input: ZoneFrameInput): ZoneFrameLayout {
  const W = Math.max(0, input.width);
  const H = Math.max(0, input.height);

  const sliverH = clamp(input.sliverHeight, 0, H);
  const mainTop = sliverH;
  const mainH = H - sliverH;

  // Tools: a fixed left rail. Never wide enough to starve the Model of its minimum.
  const toolsW = clamp(input.toolsWidth, 0, Math.max(0, W - MIN_MODEL_WIDTH));

  // Activities: right rail, drag-resizable. Clamp so the center column keeps at
  // least MIN_MODEL_WIDTH; a <= 0 request hides the rail.
  const maxActivities = Math.max(0, W - toolsW - MIN_MODEL_WIDTH);
  const activitiesW =
    input.activitiesWidth <= 0
      ? 0
      : clamp(input.activitiesWidth, Math.min(MIN_ACTIVITIES_WIDTH, maxActivities), maxActivities);

  const centerX = toolsW;
  const centerW = Math.max(0, W - toolsW - activitiesW);

  // Results: bottom of the center column, drag-resizable. Clamp so the Model keeps
  // at least MIN_MODEL_HEIGHT; a <= 0 request hides the band.
  const maxResults = Math.max(0, mainH - MIN_MODEL_HEIGHT);
  const resultsH =
    input.resultsHeight <= 0
      ? 0
      : clamp(input.resultsHeight, Math.min(MIN_RESULTS_HEIGHT, maxResults), maxResults);

  const modelH = Math.max(0, mainH - resultsH);

  return {
    sliver: { x: 0, y: 0, width: W, height: sliverH },
    tools: { x: 0, y: mainTop, width: toolsW, height: mainH },
    model: { x: centerX, y: mainTop, width: centerW, height: modelH },
    results: { x: centerX, y: mainTop + modelH, width: centerW, height: resultsH },
    activities: { x: W - activitiesW, y: mainTop, width: activitiesW, height: mainH },
  };
}

/** True if two rectangles overlap on a positive area (shared edges do not count). */
export function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    b.x < a.x + a.width &&
    a.y < b.y + b.height &&
    b.y < a.y + a.height
  );
}
